import { STORAGE_KEYS } from '@/config/storageKeys'
import {
  getValidationErrors,
  safeValidateNutritionData,
  sanitizeNutritionData,
} from '@/schemas/nutritionSchema'
import { logger } from '@/services/logger'
import { migrateStorage } from '@/services/storageVersion'
import { type DailyNutrition } from '@/types/nutrition'

/**
 * @deprecated Use STORAGE_KEYS.NUTRITION_DATA from '@/config/storageKeys' instead
 */
export const NUTRITION_STORAGE_KEY = STORAGE_KEYS.NUTRITION_DATA

/**
 * Load nutrition data from localStorage with automatic migration and validation
 * @returns Array of DailyNutrition entries, or empty array if no data or error
 */
export const loadNutritionData = (): DailyNutrition[] => {
  try {
    // Use migration system to handle version changes
    const data = migrateStorage<unknown[]>(STORAGE_KEYS.NUTRITION_DATA, [])

    if (!Array.isArray(data)) {
      logger.warn('Nutrition data is not an array, returning empty array')
      return []
    }

    // Convert date strings back to Date objects
    const dataWithDates = data.map((entry: unknown) => {
      const record = entry as Record<string, unknown>
      return {
        ...record,
        date: new Date(record.date as string),
      }
    })

    // Validate the data structure with Zod
    const validationResult = safeValidateNutritionData(dataWithDates)

    if (!validationResult.success) {
      logger.warn('Nutrition data validation failed, attempting to sanitize')
      logger.debug('Validation errors:', getValidationErrors(dataWithDates))

      // Attempt to sanitize data by removing invalid entries
      const sanitized = sanitizeNutritionData(dataWithDates)

      if (sanitized.length < data.length) {
        logger.warn(
          `Removed ${data.length - sanitized.length} invalid nutrition entries during sanitization`
        )
      }

      if (sanitized.length === 0) {
        logger.error('All nutrition data entries are invalid, returning empty array')
        return []
      }

      // Validated data structure matches DailyNutrition (branded types are compatible)
      return sanitized as unknown as DailyNutrition[]
    }

    // Validated data structure matches DailyNutrition (branded types are compatible)
    return validationResult.data as unknown as DailyNutrition[]
  } catch (error) {
    logger.error('Failed to load nutrition data from storage', error)
    return []
  }
}

/**
 * Save nutrition data to localStorage with validation
 * @param data - Array of DailyNutrition entries to save
 */
export const saveNutritionDataToStorage = (data: DailyNutrition[]): void => {
  try {
    // Validate data before saving
    const validationResult = safeValidateNutritionData(data)

    if (!validationResult.success) {
      logger.error('Cannot save invalid nutrition data')
      logger.debug('Validation errors:', getValidationErrors(data))
      throw new Error('Nutrition data validation failed')
    }

    localStorage.setItem(STORAGE_KEYS.NUTRITION_DATA, JSON.stringify(data))
  } catch (error) {
    logger.error('Failed to save nutrition data:', error)
    throw error
  }
}
